import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  HostBinding,
  input,
  Input,
  Output,
  signal,
  TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  atxIsMobile$,
  atxIsTouchScreen,
} from '@epicuro-next/ataraxia/breakpoint';
import { ProductCardType } from '@epicuro-next/ataraxia/product-card';
import {
  isclosedoutproductLabelText,
  keyarticlesLabelText,
} from '@epicuro-next/constants/features/localization';
import {
  ProductCardConfiguration,
  ProductCardConfigurationKeys,
  PRODUCT_CARD_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/product-card-rules';
import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { ListProductReference } from '@epicuro-next/data/lists';
import {
  selectConfigurationValueFrom,
  selectSizePreviewV2Enabled,
} from '@epicuro-next/platform/configuration';
import { selectShowPCardMarkup } from '@epicuro-next/state/p-basket-state';
import {
  hasGenderIndicator,
  hasIsNewIndicator,
  hasMraIndicator,
  hasPopularityIndicator,
  isNonNullish,
} from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import {
  FloatRange,
  Product,
  ProductLabelType,
  ProductMedia,
  ProductPriceRanges,
} from 'graphql-api/gql-api-interfaces';
import { combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

@Component({
  selector: 'epc-product-card-base',
  templateUrl: './product-card-base.component.html',
  styleUrls: ['./product-card-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardBaseComponent {
  /**
   * Product data
   * TODO: fix the types of the product and the prices after having the graphql API in place
   */
  @Input() public product: Product;

  /**
   * List produce reference for commercial product-card
   */
  @Input() public productReference: ListProductReference;

  /**
   * List produce reference for commercial product-card
   */
  @Input() public showCommercialListDate = false;

  /**
   * Show/hide the actions buttons.
   */
  @Input() public showActions = true;

  /**
   * Show/hide the add/remove from active list button.
   */
  @Input() public showBookmarkIcon = true;

  /**
   * Show delete from product list icon
   */
  @Input() public deletable = false;

  /**
   * Product data
   */
  @Input() public prices: ProductPriceRanges[];

  /**
   * Template to render inside drag & drop container
   */
  @Input() public dragAndDropHandleTemplate?: TemplateRef<unknown>;

  /**
   * Show delete from product list icon
   */
  @Input() public clickDisabled = false;

  @Input() public isSearchV2Enabled = false;

  @Input() public showProductTitle = true;

  /**
   * Discount max value
   */
  public discount: FloatRange | null = null;

  @HostBinding('class.unavailable')
  @Input()
  public showAsUnavailable = false;

  /**
   * Show the delete button when the
   * product is not available
   */
  @Input()
  public showUnavailableDeleteButton = false;

  @Input()
  public type = ProductCardType.Default;

  @Input() public isDraggable = false;

  public showIcons = input(true);

  // need to false for expanded view
  public showFrameLabel = input(true);

  /**
   * Output event emitted when the user clicks the card
   */
  @Output() public cardClick = new EventEmitter<string>();

  public isMobile$ = atxIsMobile$;

  public currentVariantMedia: ProductMedia[];

  public currentVariant = signal<Product | null>(null);

  public productSignal = signal<Product | null>(null);

  public readonly productFrameLabelShadow = computed(
    () =>
      this.productSignal()?.labels?.find(
        ({ labelType }) => labelType === ProductLabelType.Frame,
      )?.data?.shadow,
  );

  public readonly hasIconsIndicators = computed(
    () => !!this.productSignal()?.labels?.length,
  );
  public readonly isNewIconVisible = computed(
    () => !hasIsNewIndicator(this.productSignal()?.labels),
  );
  public readonly isPopularityIconVisible = computed(
    () => !hasPopularityIndicator(this.productSignal()?.labels),
  );
  public readonly isGenderIconVisible = computed(
    () => !hasGenderIndicator(this.productSignal()?.labels),
  );
  public readonly isMraIconVisible = computed(
    () => !hasMraIndicator(this.productSignal()?.labels),
  );

  public productLabelTranslationKeys = {
    isclosedoutproduct: isclosedoutproductLabelText,
    keyarticles: keyarticlesLabelText,
  };

  public enableProgressiveImageLoad$ = this.store.select(
    selectConfigurationValueFrom<
      ProductCardConfigurationKeys.ProgressiveImageLoad,
      ProductCardConfiguration
    >(
      PRODUCT_CARD_FEATURE_NAME,
      ProductCardConfigurationKeys.ProgressiveImageLoad,
      true,
    ),
  );
  public showMarkup$ = this.store.select(selectShowPCardMarkup);

  public showPopularityIcon$ = this.store.select(
    selectConfigurationValueFrom<
      PulseConfigurationKeys.PopularityIcon,
      PulseConfigurations
    >(PULSE_FEATURE_NAME, PulseConfigurationKeys.PopularityIcon, false),
  );

  public productLabelAttributeKeys$ = this.store.select(
    selectConfigurationValueFrom<
      PulseConfigurationKeys.ProductLabelAttributeKeys,
      PulseConfigurations
    >(
      PULSE_FEATURE_NAME,
      PulseConfigurationKeys.ProductLabelAttributeKeys,
      pulseConfigurationDefaultValues[
        PulseConfigurationKeys.ProductLabelAttributeKeys
      ],
    ),
  );

  public showVariations$ = this.store.select(
    selectConfigurationValueFrom<
      PulseConfigurationKeys.Variations,
      PulseConfigurations
    >(PULSE_FEATURE_NAME, PulseConfigurationKeys.Variations, false),
  );

  private showIconsOnHoverRule$ = this.store.select(
    selectConfigurationValueFrom<
      ProductCardConfigurationKeys.ProductShowIconsOnHover,
      ProductCardConfiguration
    >(
      PRODUCT_CARD_FEATURE_NAME,
      ProductCardConfigurationKeys.ProductShowIconsOnHover,
      true,
    ),
  );

  public readonly sizePreviewV2Enabled = toSignal(
    this.store.select(selectSizePreviewV2Enabled),
  );

  public showIconsOnHover$ = combineLatest([
    this.showIconsOnHoverRule$,
    this.isMobile$,
  ]).pipe(
    map(([rule, isMobile]) => {
      if (atxIsTouchScreen || isMobile) return false;
      return rule;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public productCardType = ProductCardType;

  constructor(protected store: Store) {}

  /**
   * Method to emit click event when user clicks the card
   */
  public clickCard() {
    if (this.clickDisabled) {
      return;
    }
    this.cardClick.emit(this.product.productId);
  }

  public get discountVisible() {
    const productPrices = (this.prices || [])[0];

    if (isNonNullish(productPrices?.discount)) {
      this.discount = {
        max: productPrices.discount.max ?? 0,
        min: 0,
      };
    }

    return (this.discount?.max ?? 0) > 0;
  }
}
